import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { CartItem, Product } from '../types';
import * as analytics from '@/lib/analytics';

interface CartContextType {
  items: CartItem[];
  addItem: (product: Product, quantity?: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getTotal: () => number;
  getItemCount: () => number;
  getProductQuantity: (productId: string) => number;
  justAdded: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [justAdded, setJustAdded] = useState(false);

  // Load from localStorage only on client side after mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('cart');
      if (saved) {
        setItems(JSON.parse(saved));
      }
    }
  }, []);

  // Save to localStorage whenever items change
  const saveToLocalStorage = useCallback((newItems: CartItem[]) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cart', JSON.stringify(newItems));
    }
  }, []);

  // Reset highlight after animation
  useEffect(() => {
    if (justAdded) {
      const timer = setTimeout(() => setJustAdded(false), 600);
      return () => clearTimeout(timer);
    }
  }, [justAdded]);

  const addItem = useCallback((product: Product, quantity: number = 1) => {
    // Prevent adding out-of-stock items
    if (!product.inStock) {
      console.warn('Cannot add out-of-stock product to cart:', product.name);
      return;
    }

    setItems(prevItems => {
      const existingItem = prevItems.find(item => item.product.id === product.id);
      let newItems: CartItem[];
      
      if (existingItem) {
        // Check if the existing item is still in stock
        if (!existingItem.product.inStock) {
          console.warn('Cannot update quantity for out-of-stock product:', product.name);
          return prevItems;
        }
        newItems = prevItems.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        newItems = [...prevItems, { product, quantity }];
      }
      
      // Track add to cart event
      analytics.addToCart({
        item_id: product.id,
        item_name: product.name,
        price: product.price,
        quantity,
      }, quantity);
      
      saveToLocalStorage(newItems);
      setJustAdded(true);
      return newItems;
    });
  }, [saveToLocalStorage]);

  const removeItem = useCallback((productId: string) => {
    setItems(prevItems => {
      const itemToRemove = prevItems.find(item => item.product.id === productId);
      
      // Track remove from cart event
      if (itemToRemove) {
        analytics.removeFromCart({
          item_id: itemToRemove.product.id,
          item_name: itemToRemove.product.name,
          price: itemToRemove.product.price,
        }, itemToRemove.quantity);
      }
      
      const newItems = prevItems.filter(item => item.product.id !== productId);
      saveToLocalStorage(newItems);
      return newItems;
    });
  }, [saveToLocalStorage]);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }
    
    setItems(prevItems => {
      const item = prevItems.find(item => item.product.id === productId);
      
      // Prevent updating quantity for out-of-stock items
      if (item && !item.product.inStock) {
        console.warn('Cannot update quantity for out-of-stock product');
        return prevItems;
      }
      
      const newItems = prevItems.map(item =>
        item.product.id === productId
          ? { ...item, quantity }
          : item
      );
      saveToLocalStorage(newItems);
      return newItems;
    });
  }, [removeItem, saveToLocalStorage]);

  const clearCart = useCallback(() => {
    setItems([]);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('cart');
    }
  }, []);

  const getTotal = useCallback(() => {
    return items.reduce((total, item) => total + item.product.price * item.quantity, 0);
  }, [items]);

  const getItemCount = useCallback(() => {
    return items.reduce((count, item) => count + item.quantity, 0);
  }, [items]);

  const getProductQuantity = useCallback((productId: string) => {
    const item = items.find(item => item.product.id === productId);
    return item ? item.quantity : 0;
  }, [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        getTotal,
        getItemCount,
        getProductQuantity,
        justAdded,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

